// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';

import { blockypDefault, Color, flex, Fonts, topMargin, Width } from 'styles';
import { useImages } from 'hooks';
import type { AllImages } from 'hooks/use-images';
import { ImageLoader } from 'components/Gui';

const Wrapper = styled.div`
  ${flex()};
  ${({ marginTop }) => topMargin(...marginTop)};
  flex-direction: column;

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;

    p {
      width: 296px;
      margin: 0 auto;
    }
  }

  span,
  p {
    text-align: center;
    color: ${Color.PURPLE_FIGURE};
  }

  p {
    ${blockypDefault};
  }
`;

const Item = styled.div`
  width: 100%;
  flex: 0 0 auto;
  ${flex()};
  flex-direction: column;

  p {
    margin-bottom: 40px;
  }

  &:last-of-type {
    p {
      margin-bottom: 0;
    }
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    max-width: 373px;

    p {
      margin-bottom: 0;
    }
  }

  .images {
    ${flex()};
    margin-bottom: 24px;
    width: 100%;
    max-width: 373px;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: 373px;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 50%;
  flex: 0 0 auto;
  height: 142px;
  ${flex()};
  flex-direction: column;
  box-sizing: border-box;

  span {
    ${flex()};
    ${Fonts.POPPINS};
    font-weight: 700;
    font-size: 1.3rem;
    letter-spacing: 0.035rem;
  }

  ${({ border }) =>
    border &&
    css`
      border-right: 2px solid ${Color.GREY_LIGHT};

      span {
        color: ${Color.GREY_DARK};
      }
    `};
`;

const Image = styled.div`
  width: ${({ width }) => width}px;
  height: 120px;
  ${flex()};
`;

type Props = {
  marginTop?: number[],
  content: {
    images: {
      name: string,
      width: number,
      alt?: string,
      caption: string,
    }[],
    blurb: Node | string,
  }[],
  images: AllImages,
};

const BlockyComparisonList = ({ marginTop = [0, 0, 0], content, images, ...rest }: Props) => {
  const getImage = useImages(images);
  return (
    <Wrapper {...rest} marginTop={marginTop} role="list">
      {content.map(({ images: contentImages, blurb }, index) => (
        <Item role="listitem" key={`BlockyComparisonList-${index}`}>
          <div className="images">
            {contentImages.map(({ name, width, alt, caption }, innerIndex) => (
              <ImageWrapper key={`BlockyComparisonListImage-${index}-${innerIndex}`} border={innerIndex === 0}>
                <Image width={width}>
                  <ImageLoader image={getImage(name)} alt={alt} />
                </Image>
                <span>{caption}</span>
              </ImageWrapper>
            ))}
          </div>
          {blurb}
        </Item>
      ))}
    </Wrapper>
  );
};

BlockyComparisonList.contentType = 'BlockyComparisonList';

export default BlockyComparisonList;
